/*
 * <lurk-tier-service> — one toggle, both tier cards.
 *
 * The customization product crosses two option axes: the privacy tier (Public /
 * Private) and the design service (I have my design / Design it with us).
 * Four flat variants would make a two-part decision look like four products,
 * so the page keeps two tier cards and this element re-points each card's form
 * at its other variant when the shared toggle changes.
 *
 * Progressive enhancement, deliberately: the control ships hidden and is
 * revealed here. Without JS the cards stay on their base variants — which is
 * what an unchecked toggle would have meant anyway — and the design service is
 * still reachable from the product page's own variant picker. Nothing is
 * offered that can't be honoured.
 */
class LurkTierService extends HTMLElement {
  connectedCallback() {
    this.control = this.querySelector('[data-service-control]');
    this.toggle = this.querySelector('[data-service-toggle]');
    this.note = this.querySelector('[data-service-note]');
    this.tiers = Array.from(this.querySelectorAll('[data-tier]'));

    if (!this.control || !this.toggle || this.tiers.length === 0) return;

    // Only offer the toggle if every tier can actually honour it.
    if (!this.tiers.every((tier) => tier.dataset.variantService)) return;

    this.control.hidden = false;
    this.toggle.addEventListener('change', () => this.apply());
    this.apply();
  }

  apply() {
    const on = this.toggle.checked;

    this.tiers.forEach((tier) => {
      const id = on ? tier.dataset.variantService : tier.dataset.variantBase;
      const label = on ? tier.dataset.labelService : tier.dataset.labelBase;
      const available = (on ? tier.dataset.availService : tier.dataset.availBase) === '1';

      const input = tier.querySelector('[data-variant-input]');
      if (input && id) input.value = id;

      const base = tier.querySelector('[data-price-base]');
      const service = tier.querySelector('[data-price-service]');
      if (base && service) {
        base.hidden = on;
        service.hidden = !on;
      }

      const button = tier.querySelector('[data-tier-add]');
      if (!button) return;

      if (label) button.setAttribute('aria-label', label);

      button.disabled = !available;
      if (available) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }

      const text = button.querySelector('[data-add-text]');
      if (text) {
        text.textContent = available
          ? tier.dataset.addLabel || text.textContent
          : tier.dataset.soldOutLabel || text.textContent;
      }
    });

    // The note is a live region, so turning the service on announces what it
    // commits you to rather than only changing two numbers on screen.
    if (this.note) this.note.hidden = !on;
  }
}

if (!customElements.get('lurk-tier-service')) {
  customElements.define('lurk-tier-service', LurkTierService);
}
